// @flow
import memoize from 'lodash/fp/memoize';
import uniqBy from 'lodash/fp/uniqBy';
import sortBy from 'lodash/fp/sortBy';
import last from 'lodash/fp/last';
import head from 'lodash/fp/head';
import parse from 'date-fns/fp/parse';
import * as React from 'react';

// The lodash FP Guide says that memoize has a fixed arity of one.
// This allows us to have a resolver.
// see https://github.com/lodash/lodash/issues/3321 for more
const memoizeWithResolver = (memoize: any).convert({ fixed: false });

// eslint-disable-next-line no-unused-vars
/* global TEST_TODAY */
declare var TEST_TODAY: any;

type Adjustment = $ReadOnly<{|
  fullYear?: number,
  month?: number,
  date?: number,
  hours?: number,
  minutes?: number,
  seconds?: number,
  milliseconds?: number,
|}>;

export type Format = string;

type MonthNumericalValue = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export const withAdjustment = (adjustment: Adjustment, date: Date): Date =>
  new Date(
    typeof adjustment.fullYear === 'number'
      ? adjustment.fullYear
      : date.getFullYear(),
    typeof adjustment.month === 'number' ? adjustment.month : date.getMonth(),
    typeof adjustment.date === 'number' ? adjustment.date : date.getDate(),
    typeof adjustment.hours === 'number' ? adjustment.hours : date.getHours(),
    typeof adjustment.minutes === 'number'
      ? adjustment.minutes
      : date.getMinutes(),
    typeof adjustment.seconds === 'number'
      ? adjustment.seconds
      : date.getSeconds(),
    typeof adjustment.milliseconds === 'number'
      ? adjustment.milliseconds
      : date.getMilliseconds(),
  );

export const atStartOfDay = (date: Date): Date =>
  withAdjustment(
    {
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    },
    date,
  );

export const atStartOfISODay = (date: Date): Date =>
  withAdjustment(
    {
      minutes: -date.getTimezoneOffset(),
    },
    atStartOfDay(date),
  );

export const stringAtStartOfISODay = (date: Date): string => {
  const dt = atStartOfISODay(date);
  return dt.toISOString();
};

export const atMidday = (date: Date): Date =>
  withAdjustment(
    {
      hours: 12,
    },
    atStartOfDay(date),
  );

export const toDate = (str: string | Date | number | null): Date => {
  if (!str) return new Date(0);

  if (typeof str === 'string') {
    const match = str.match(/(^(\d{4})-(\d{2})-(\d{2}))/);
    if (!match) return new Date(0);

    const [, , year, month, day] = match;
    const dt = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      0,
      0,
      0,
      0,
    );
    return dt;
  }

  if (typeof str === 'number') {
    return atStartOfDay(new Date(str));
  }

  return atStartOfDay(str);
};

export const atFirstOfMonth = (date: Date): Date =>
  atStartOfDay(withAdjustment({ date: 1 }, date));

export const today = (): Date =>
  toDate(typeof TEST_TODAY === 'undefined' ? new Date() : TEST_TODAY);

export const dateString = (date: Date): string =>
  [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map(String)
    .map((s) => s.padStart(2, '0'))
    .join('-');

export const dateStringNonPadded = (date: Date): string =>
  [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map(String)
    .join('-');

export const dateStringMonthYear = (date: Date): string =>
  [date.getFullYear(), date.getMonth() + 1]
    .map(String)
    .map((s) => s.padStart(2, '0'))
    .join('-');

const formatterForLocale: (
  locale: string,
  options?: Intl$DateTimeFormatOptions,
) => Intl$DateTimeFormat = memoizeWithResolver(
  (locale: string, options?: Intl$DateTimeFormatOptions) => {
    // The default value for each date-time component property is undefined, but
    // if all component properties are undefined, then year, month, and day are
    // assumed to be "numeric".
    const defaultOptions = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    };
    return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options });
  },
  // this is the resolver for the cache
  (...args) => JSON.stringify(args),
);

export const localDateString = (
  date: Date | string | number,
  locale: string = 'en-us',
  options?: Intl$DateTimeFormatOptions,
): string => {
  const dateAsDate = toDate(date);
  const kebabLocale = locale && locale.replace(/_/g, '-');

  return formatterForLocale(kebabLocale, options).format(dateAsDate);
};

export const fullMonthAndYearString = (
  date: Date,
  locale: string = 'en-us',
): string =>
  new Intl.DateTimeFormat(locale.replace(/_/g, '-'), {
    year: 'numeric',
    month: 'long',
  }).format(date);

export const isBefore = (date1: Date, date2: Date): boolean => date1 < date2;

export const isBeforeOrSame = (date1: Date, date2: Date): boolean =>
  date1 <= date2;

export const isLater = (date1: Date, date2: Date): boolean => date1 > date2;

export const isAfter = isLater;

export const isLaterOrSame = (date1: Date, date2: Date): boolean =>
  date1 >= date2;

export const isAfterOrSame = isLaterOrSame;

export const isSameDay = (date1: Date, date2: Date): boolean =>
  date1.toDateString() === date2.toDateString();

export const earliestToLatest = (dates: Date[]): Date[] =>
  sortBy((date) => date.valueOf(), dates);

export const earlierOf = (a: Date, b: Date): Date => (a < b ? a : b);

export const laterOf = (a: Date, b: Date): Date => (a > b ? a : b);

export const earliest = (dates: Date[]): Date | null =>
  head(earliestToLatest(dates)) || null;

export const latest = (dates: Date[]): Date | null =>
  last(earliestToLatest(dates)) || null;

export const uniqDates = <T: null | Date>(dates: T[]): T[] =>
  uniqBy((date) => (date === null ? date : date.getTime()))(dates);

export const unixTimeMs = (date: Date): number => date.getTime();

// really this should be further in the future, but any further breaks MockDate
export const farFuture: Date = new Date(2035, 0, 1, 0, 0, 0, 0);

export const farPast: Date = new Date(1980, 0, 1, 0, 0, 0, 0);

export const isoDatePart = (date: Date): string =>
  date.toISOString().split('T')[0];

export const comparedAsDates = (a: Date, b: Date): number => {
  const aDate = isoDatePart(a);
  const bDate = isoDatePart(b);

  if (aDate < bDate) return -1;
  if (aDate > bDate) return 1;
  return 0;
};

export const key = (date: Date): string => date.toString();

export const withDayOffset = (offset: number, date: Date): Date =>
  withAdjustment({ date: date.getDate() + offset }, date);

export const withMonthOffset = (offset: number, date: Date): Date =>
  withAdjustment({ month: date.getMonth() + offset }, date);

export const yearOfDate = (date: Date): number => date.getFullYear();

export const monthOfDate = (date: Date): MonthNumericalValue =>
  ((date.getMonth(): any): MonthNumericalValue);

/**
 * Predicate: Is the date valid?
 *
 * Invalid dates are the result of performing impossible datewise operations or
 * parsing bad input.
 *
 * @example
 * isValid(new Date()); // true
 * isValid(new Date('junk')); // false
 */
export const isValid = (date: Date): boolean => !Number.isNaN(date.valueOf());

/**
 * Predicate: Does the value of the date match the unix epoch (Jan. 1, 1970) ?
 *
 * The unix epoch is the date at which a Date's internal state is actually 0.
 *
 * @example
 * isEpoch(new Date()); // false
 * isEpoch(new Date(null)); // true
 * isEpoch(new Date(0)); // true
 */
export const isEpoch = (date: Date): boolean => date.valueOf() === 0;

/**
 * The given string parsed as a Date using the given format.
 *
 * Uses Unicode date symbols.
 *
 * Note that this can throw an exception if the format string is somehow impossible.
 * Note also that this can result in an invalid date.
 *
 * @example
 * fromString('M/d/Y')('11/4/2000') // Date
 *
 * @see https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 */
export const fromString: (format: string) => (str: string) => Date = parse(
  today(),
);

/**
 * The given string parsed as a Date using the given format, or null if parsing
 * resulted in an invalid date.
 *
 * Uses Unicode date symbols.
 *
 * Note that this can throw an exception if the format string is somehow impossible.
 *
 * A resultant Date is considered invalid if it is marked by JS as an invalid date, or
 * if it matches the Unix epoch.
 *
 * @example
 * fromString('M/d/Y')('11/4/2000') // Date
 * fromString('Y/d/M')('11/4/2000') // null
 * fromString('M/d/Y')('junk') // null
 *
 * @see https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 *
 */
export const validFromString: (
  format: string,
) => (str: string) => Date | null = (format: string) => (str: string) => {
  const dt = fromString(format)(str);

  if (!isValid(dt)) return null;
  if (isEpoch(dt)) return null;

  return dt;
};
export const secondsToTimeString = (
  seconds: number,
  excludeHours?: boolean,
): string =>
  [
    String(Math.floor(seconds / 3600)).padStart(2, '0'),
    String(Math.floor((seconds % 3600) / 60)).padStart(2, '0'),
    String(Math.floor((seconds % 3600) % 60)).padStart(2, '0'),
  ]
    .slice(excludeHours ? 1 : 0)
    .join(':');

type Preset = $ReadOnly<{|
  label: string,
  value: $ReadOnly<{|
    range: $ReadOnlyArray<number>,
    field: string,
    relevant_from?: number,
  |}>,
|}>;

export type Presets = $ReadOnlyArray<Preset>;

export type DateOption = {
  label: string,
  value: {
    range: $ReadOnlyArray<Date>,
    relevant_from?: Date | null,
  },
};

export type TimeFrameOption = $ReadOnly<{|
  field: string,
  range: $ReadOnlyArray<Date>,
|}>;

export type ButtonOption = $ReadOnly<{|
  label: React.Node,
  key: string,
  value: {
    range: $ReadOnlyArray<Date>,
    relevant_from?: Date | null,
  },
|}>;

export type ButtonOptions = $ReadOnlyArray<ButtonOption>;

export const dateOptions = (presets: Presets): $ReadOnlyArray<DateOption> =>
  presets.map((option) => {
    const { value } = option;
    const from = value.range && value.range[0];
    const to = value.range && value.range[1];
    const relevantFrom = value.relevant_from || 0;
    const fromDate = today();
    const todayDate = today();
    const relevantDate = today();

    return {
      ...option,
      value: {
        range: [
          withDayOffset(from, fromDate),
          withDayOffset(to, todayDate),
        ].filter(Boolean),
        relevant_from: withDayOffset(relevantFrom, relevantDate),
      },
    };
  });

export const daysBetween = (date: Date, secondDate: Date): number => {
  const difference = date.getTime() - secondDate.getTime();
  return difference / (1000 * 3600 * 24);
};
